import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * The "My accounts" filter — Stage 3 of the account-visibility mandate
 * ("scope becomes a FILTER, not a GATE").
 *
 * Scope used to be a GATE: a rep simply could not reach another rep's account.
 * It is now a DEFAULT-ON, CLEARABLE filter. This module is the single client
 * store for that one bit, shared by every Class A (BROWSE/LOOKUP) surface —
 * the accounts list, search, the pipeline/deal boards, the QBR portfolio, the
 * churn-risk queue and prospecting — so the whole app says "my book" or "all
 * accounts" at once instead of each page inventing its own answer.
 *
 * Three rules this file exists to keep honest:
 *
 *  1. THE DEFAULT LIVES ON THE SERVER (AC-2b). We only ever SEND `mine=false`.
 *     Omitting the parameter means "filter ON", so every existing bookmark,
 *     saved view, deep link and internal script keeps today's result and the
 *     filter-ON request URLs stay byte-identical to what shipped before.
 *  2. `available: false` is NOT "you own nothing" (DS-3). It means the server
 *     could not resolve the viewer's book at all (a HubSpot outage), degraded
 *     to showing everything with the control switched off. The UI must never
 *     imply "you own everything" in that state, so the toggle is disabled and
 *     labelled — see AccountFilterToggle.
 *  3. Admin/editor (`scoped: false`) is not offered the filter at all; they
 *     were never scoped, and their result sets must stay byte-identical.
 */

const STORAGE_KEY = 'funnel.accountFilter.mine';

/** The `accountFilter` discriminator every Class A response echoes. */
export interface AccountFilterEcho {
  available: boolean;
  defaultOn: boolean;
}

export interface AccountFilterValue {
  /**
   * Is the "My accounts" filter ON right now? Always `true` for an unscoped
   * admin (there is nothing to filter) and always `false` when the server
   * reported the filter unavailable — so this bit always matches what the
   * server actually applied, which is what the KPI labels key on (AC-7b).
   */
  mine: boolean;
  /** Flip the filter. Callers MUST also reset their own paging (AC-2d). */
  setMine: (next: boolean) => void;
  /** Is this viewer account-scoped (a rep)? Admin/editor ⇒ false. */
  scoped: boolean;
  /** DS-3: could the server resolve this viewer's book? */
  available: boolean;
  /** DS-3 discriminator: scoped, but resolution FAILED. */
  unresolved: boolean;
  /** Feed a Class A response's echoed `accountFilter` back into the store. */
  reportAvailability: (echo: AccountFilterEcho | undefined | null) => void;
}

/**
 * Inert default, used when a component renders outside the provider (isolated
 * component tests, and any surface that predates this feature). "Not scoped"
 * means: no toggle rendered, no `mine` parameter sent — i.e. exactly today's
 * behaviour, never an accidental widening.
 */
const INERT: AccountFilterValue = {
  mine: true,
  setMine: () => undefined,
  scoped: false,
  available: true,
  unresolved: false,
  reportAvailability: () => undefined,
};

const AccountFilterContext = createContext<AccountFilterValue | null>(null);

function readPersisted(): boolean {
  try {
    // Only an explicit "false" clears it — anything else (absent, corrupt,
    // private-mode throw) falls back to the server default, ON.
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function AccountFilterProvider({
  children,
  accountScoped,
}: {
  children: ReactNode;
  /** From `/api/auth/me` — true for a viewer whose reads were owner-scoped. */
  accountScoped: boolean;
}) {
  const [mineState, setMineState] = useState<boolean>(readPersisted);
  const [available, setAvailable] = useState(true);

  const setMine = useCallback((next: boolean) => {
    setMineState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
    } catch {
      /* private mode / quota — the choice just doesn't survive a reload */
    }
  }, []);

  const reportAvailability = useCallback((echo: AccountFilterEcho | undefined | null) => {
    if (!echo || typeof echo.available !== 'boolean') return;
    setAvailable(echo.available);
  }, []);

  const value = useMemo<AccountFilterValue>(() => {
    // An unscoped admin is never filtered; an unresolved scope is forced OFF
    // server-side (DS-3), so mirror both here rather than letting the stored
    // preference disagree with the rows on screen.
    const mine = !accountScoped ? true : available ? mineState : false;
    return {
      mine,
      setMine,
      scoped: accountScoped,
      available,
      unresolved: accountScoped && !available,
      reportAvailability,
    };
  }, [accountScoped, available, mineState, setMine, reportAvailability]);

  return <AccountFilterContext.Provider value={value}>{children}</AccountFilterContext.Provider>;
}

export function useAccountFilter(): AccountFilterValue {
  return useContext(AccountFilterContext) ?? INERT;
}

/**
 * Append the `mine` parameter to a Class A request URL — and ONLY when the
 * filter is cleared. The server default is ON (AC-2b), so a filter-ON URL is
 * byte-identical to the one this app has always sent (which also keeps the
 * useCachedFetch cache key stable).
 */
export function withMine(url: string, mine: boolean): string {
  if (mine) return url;
  return `${url}${url.includes('?') ? '&' : '?'}mine=false`;
}

/**
 * The label every KPI/section that follows the filter must render alongside its
 * number. AC-7b: "Customers: 12" silently becoming "Customers: ~700" under an
 * unchanged label is the specific failure this exists to prevent.
 */
export function accountScopeLabel(mine: boolean): string {
  return mine ? 'your book' : 'all accounts';
}
